
import React, { useContext, useState } from "react";
import {
    Box,
    Button,
    CssBaseline,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    Link,
    ThemeProvider,
    createTheme,
} from "@mui/material";
import AuthContext from "../context/AuthContext";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "./MyStyles.css";

const theme = createTheme({
    palette: {
        primary: { main: "#087f8c" },
        secondary: { main: "#20b486" },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Arial", sans-serif',
        button: { textTransform: "none", fontWeight: 700 },
    },
});

function Copyright() {
    return (
        <Typography className="login-copyright">
            © {new Date().getFullYear()}{" "}
            <Link
                href="https://www.mgmebuild.com/"
                target="_blank"
                rel="noreferrer"
                underline="none"
                color="inherit"
            >
                MGM eBuild Pvt Ltd
            </Link>
            {" "}· All rights reserved
        </Typography>
    );
}

export default function Login() {
    const { loginUser } = useContext(AuthContext);

    const [loginData, setLoginData] = useState({
        hospital_code: "",
        username: "",
        password: "",
    });

    const [showPassword, setShowPassword] = useState(false);

    const [formError, setFormError] = useState("");
    const [isLicenseExpired] = useState(false);

    const inputHandler = (event) => {
        const { name, value } = event.target;

        setLoginData((previous) => ({
            ...previous,
            [name]:
                name === "hospital_code"
                    ? value.toUpperCase().replace(/[^A-Z0-9]/g, "")
                    : value,
        }));

        if (formError) setFormError("");
    };

    const buttonEnable =
        loginData.hospital_code.trim() !== "" &&
        loginData.username.trim() !== "" &&
        loginData.password !== "";


const handleSubmit = async (event) => {
    event.preventDefault();

    if (!buttonEnable || isLicenseExpired) return;

    setFormError("");

    try {
        const result = await loginUser(
            loginData.hospital_code.trim(),
            loginData.username.trim(),
            loginData.password
        );

        if (!result?.success) {
            setFormError(result?.error || "Unable to sign in. Please check your credentials.");
        }
    } catch (error) {
        console.error("Login submission failed:", error);

        setFormError(
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            error?.message ||
            "Unable to sign in. Please check your credentials."
        );
    }
};


    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            <main className="hospital-login">
                {/* Background */}
                <div className="medical-background" aria-hidden="true">
                    <div className="medical-grid" />
                    <div className="medical-orb medical-orb-one" />
                    <div className="medical-orb medical-orb-two" />
                    <div className="medical-orb medical-orb-three" />

                    <div className="medical-symbol symbol-one">+</div>
                    <div className="medical-symbol symbol-two">✚</div>
                    <div className="medical-symbol symbol-three">+</div>

                    <div className="medical-ring ring-one" />
                    <div className="medical-ring ring-two" />
                </div>

                {/* Top brand */}
                <header className="login-topbar">
                    <div className="topbar-brand">
                        <span className="topbar-brand-icon">
                            <i className="fa-solid fa-heart-pulse" />
                        </span>

                        <span>
                            <strong>MGM eBuild</strong>
                            <small>Healthcare Technology</small>
                        </span>
                    </div>

                    <div className="topbar-status">
                        <span className="online-dot" />
                        DIGITAL HEALTHCARE PLATFORM
                    </div>
                </header>

                {/* Main layout */}
                <section className="login-layout">
                    {/* Left promotional panel */}
                    <div className="login-intro">
                        <div className="intro-eyebrow">
                            <span className="intro-eyebrow-icon">
                                <i className="fa-solid fa-shield-heart" />
                            </span>
                            SMARTER HEALTHCARE. CONNECTED CARE.
                        </div>

                        <h1 className="intro-heading">
                            Healthcare,
                            <br />
                            <span>reimagined.</span>
                        </h1>

                        <div className="intro-feature-list">
                            <div className="intro-feature">
                                <span className="feature-icon">
                                    <i className="fa-solid fa-hospital" />
                                </span>
                                <div>
                                    <strong>Connected Care</strong>
                                    <small>Unified hospital workflows</small>
                                </div>
                            </div>

                            <div className="intro-feature">
                                <span className="feature-icon">
                                    <i className="fa-solid fa-brain" />
                                </span>
                                <div>
                                    <strong>Intelligent Healthcare</strong>
                                    <small>AI-enabled clinical workflows</small>
                                </div>
                            </div>

                            <div className="intro-feature">
                                <span className="feature-icon">
                                    <i className="fa-solid fa-lock" />
                                </span>
                                <div>
                                    <strong>Secure Access</strong>
                                    <small>Authenticated user access</small>
                                </div>
                            </div>
                        </div>

                        <div className="intro-bottom-note">
                            <span className="intro-note-line" />
                            <span>Powered by MGM DrHiS</span>
                        </div>
                    </div>

                    {/* Login card */}
                    <Paper elevation={0} className="hospital-login-card">
                        <div className="login-card-accent" />

                        <div className="login-card-inner">
                            {/* Logo */}
                            <div className="brand-logo-stage">
                                <div className="logo-orbit logo-orbit-one" />
                                <div className="logo-orbit logo-orbit-two" />
                                <div className="logo-aura" />

                                <div className="brand-logo-frame">
                                    <img
                                        src="/mgm.png"
                                        alt="MGM DrHiS logo"
                                        className="brand-logo"
                                    />
                                </div>

                                <span className="logo-status-badge">
                                    <i className="fa-solid fa-check" />
                                </span>
                            </div>

                            <div className="login-heading">
                                <Typography component="h2" className="login-product-title">
                                    MGM <span>DrHiS</span>
                                </Typography>

                                <Typography className="login-product-subtitle">
                                    Hospital Intelligence System
                                </Typography>
                            </div>

                            {isLicenseExpired && (
                                <div className="login-alert">
                                    <i className="fa-solid fa-triangle-exclamation" />
                                    License expired. Contact your administrator.
                                </div>
                            )}

                            {formError && (
                                <div className="login-alert">
                                    <i className="fa-solid fa-circle-exclamation" />
                                    {formError}
                                </div>
                            )}

                            <Box
                                component="form"
                                onSubmit={handleSubmit}
                                className="hospital-login-form"
                                noValidate
                            >
                                {/* Hospital code */}
                                <div className="login-field-group">
                                    <label htmlFor="hospital_code">
                                        Hospital Code
                                    </label>

                                    <TextField
                                        id="hospital_code"
                                        name="hospital_code"
                                        placeholder="Enter hospital code"
                                        value={loginData.hospital_code}
                                        onChange={inputHandler}
                                        fullWidth
                                        autoFocus
                                        disabled={isLicenseExpired}
                                        autoComplete="organization"
                                        inputProps={{
                                            maxLength: 30,
                                            "aria-label": "Hospital Code",
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <span className="login-input-icon">
                                                        <i className="fa-solid fa-hospital" />
                                                    </span>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>

                                {/* Username */}
                                <div className="login-field-group">
                                    <label htmlFor="username">
                                        Username
                                    </label>

                                    <TextField
                                        id="username"
                                        name="username"
                                        placeholder="Enter your username"
                                        value={loginData.username}
                                        onChange={inputHandler}
                                        fullWidth
                                        disabled={isLicenseExpired}
                                        autoComplete="username"
                                        inputProps={{
                                            "aria-label": "Username",
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <span className="login-input-icon">
                                                        <i className="fa-solid fa-user" />
                                                    </span>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>

                                {/* Password */}
                                <div className="login-field-group">
                                    <label htmlFor="password">
                                        Password
                                    </label>

                                    <TextField
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Enter your password"
                                        value={loginData.password}
                                        onChange={inputHandler}
                                        fullWidth
                                        disabled={isLicenseExpired}
                                        autoComplete="current-password"
                                        inputProps={{
                                            "aria-label": "Password",
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <span className="login-input-icon">
                                                        <i className="fa-solid fa-lock" />
                                                    </span>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label={
                                                            showPassword
                                                                ? "Hide password"
                                                                : "Show password"
                                                        }
                                                        onClick={() =>
                                                            setShowPassword((previous) => !previous)
                                                        }
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword
                                                            ? <VisibilityOff />
                                                            : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </div>

                                {/* Submit */}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={!buttonEnable || isLicenseExpired}
                                    className="hospital-login-button"
                                    disableElevation
                                >
                                    <span className="login-button-content">
                                        <i className="fa-solid fa-right-to-bracket" />
                                        Sign In Securely
                                        <i className="fa-solid fa-arrow-right login-button-arrow" />
                                    </span>
                                </Button>
                            </Box>

                            <div className="login-security">
                                <span className="security-icon">
                                    <i className="fa-solid fa-shield-halved" />
                                </span>
                                <div>
                                    <strong>Secure sign-in</strong>
                                    <small>Use your authorized hospital account</small>
                                </div>
                            </div>

                            <div className="login-card-footer">
                                <span>DRHiS · Digital Healthcare</span>
                                <span className="footer-heart">
                                    <i className="fa-solid fa-heart-pulse" />
                                </span>
                            </div>
                        </div>
                    </Paper>
                </section>

                <footer className="hospital-login-footer">
                    <Copyright />

                    <span className="footer-platform-label">
                        MGM eBuild <span>•</span> Healthcare Technology
                    </span>
                </footer>
            </main>
        </ThemeProvider>
    );
}
